import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { getLoginScreenState } from "@features/auth/helpers/login";
import { usePollUserPublicMetadata } from "@features/auth/hooks/usePollUserPublicMetadata";
import useClerkAuth from "@hooks/useClerkAuth";
import {
	formatError,
	isIncorrectPasscodeError,
	isIncorrectPasswordError,
	isNonActionableAuthError,
	isSignUpError,
	isTestModeEmail,
} from "@hooks/useClerkAuth/helpers";
import DialogContactSupport from "@memorang/ui/components/Auth/dialogs/ContactSupportDialog";
import Loader from "@memorang/ui/components/Loader";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Keyboard } from "react-native";
import EmailView from "../components/EmailView";

type Props = {
	companyName: string;
	buildType: string;
	currentAppVersion: string;
	redirectUrl: string;
	handleTracking: (data: Record<string, string>) => void;
	supportEmail: string;
	emailFromUrl?: string;
	lastLoggedInEmail?: string;
	appId: string;
	tos?: string;
	privacy?: string;
	tenantId: string;
	onError?: (message: string) => void;
	logError: (error: unknown, title: string) => void;
};

const EmailContainer = ({
	companyName,
	handleTracking,
	supportEmail,
	buildType,
	currentAppVersion,
	redirectUrl,
	emailFromUrl,
	lastLoggedInEmail,
	appId,
	tenantId,
	tos,
	privacy,
	onError,
	logError,
}: Props) => {
	const router = useRouter();
	const [showContactSupportDialog, setShowContactSupportDialog] =
		useState(false);
	const [showPasswordField, setShowPasswordField] = useState(false);

	const { t } = useTranslation();

	const { isSignedIn } = useAuthContext();
	const [email, setEmail] = useState<string | undefined>(undefined);
	const [isTestModeAuthInProgress, setIsTestModeAuthInProgress] =
		useState(false);

	const pollQueryKey = useMemo(() => ["verify-email", email], [email]);
	const {
		userLogicalId,
		isWaitingForPublicMetadata,
		hasTimedOut,
		resetTimeout,
	} = usePollUserPublicMetadata({
		enabled: isTestModeAuthInProgress || isSignedIn,
		queryKey: pollQueryKey,
	});

	const { redeemCode } = useLocalSearchParams<{ redeemCode: string }>();

	const loginState = getLoginScreenState({
		isSignedIn,
		userLogicalId,
		redeemCode,
		hasTimedOut,
	});

	useEffect(() => {
		if (!hasTimedOut) {
			return;
		}
		setIsTestModeAuthInProgress(false);
		onError?.(t("auth.verify.accountSetupTimeoutBody"));
	}, [hasTimedOut, onError, t]);

	const { authenticate, isLoading, isAuthInProgress } = useClerkAuth({
		appId,
		tenantId,
		redirectUrl,
		onEmailLinkVerify: (email: string) => {
			router.push(
				`/(auth)/verify?email=${email}${redeemCode ? `&redeemCode=${redeemCode}` : ""}`,
			);
		},
		onEmailCodeSuccess: () => {
			setIsTestModeAuthInProgress(true);
		},
	});

	if (isLoading) {
		return <Loader />;
	}

	if (loginState.type === "redirect") {
		return <Redirect href={loginState.href} />;
	}

	const onChangeEmail = (email: string) => {
		setShowPasswordField(isTestModeEmail(email));
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const handleSubmit = async (email: string, password?: string) => {
		Keyboard.dismiss();
		setEmail(email);
		resetTimeout();
		setIsTestModeAuthInProgress(isTestModeEmail(email));
		try {
			await authenticate(email, password);
		} catch (error: unknown) {
			setIsTestModeAuthInProgress(false);
			if (isIncorrectPasscodeError(error)) {
				onError?.(t("auth.incorrectPasscode"));
			} else if (isIncorrectPasswordError(error)) {
				onError?.(t("auth.incorrectPassword"));
			} else {
				const isSignUp = isSignUpError(error);
				const longMessage = formatError(error);
				if (isNonActionableAuthError(error)) {
					onError?.(longMessage);
					return;
				}
				logError(
					longMessage,
					`Error during ${password ? "test mode " : ""}${isSignUp ? "sign-up" : "sign-in"}`,
				);
				setShowContactSupportDialog(true);
				handleTracking({
					eventName: isSignUp ? "mem_signup_attempted" : "mem_login_attempted",
					email,
				});
			}
		}
	};

	return (
		<>
			<EmailView
				handleSubmit={handleSubmit}
				loading={
					isAuthInProgress ||
					isWaitingForPublicMetadata ||
					loginState.type === "awaiting-metadata"
				}
				emailFromUrl={emailFromUrl}
				lastLoggedInEmail={lastLoggedInEmail}
				tos={tos}
				privacy={privacy}
				showPasswordField={showPasswordField}
				onChangeEmail={onChangeEmail}
			/>
			{showContactSupportDialog && (
				<DialogContactSupport
					visible={showContactSupportDialog}
					companyName={companyName}
					supportEmail={supportEmail}
					buildType={buildType}
					currentAppVersion={currentAppVersion}
					handleDismiss={() => setShowContactSupportDialog(false)}
				/>
			)}
		</>
	);
};

export default EmailContainer;
